import { useHandler } from "@redotech/react-util/hook";
import CheckIcon from "@redotech/redo-icons/check.svg";
import { FrontendProduct } from "@redotech/redo-model/agents/support-agents/frontend-product";
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import { RedoListItem } from "../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../flex";
import { Text } from "../text";
import * as productTileCss from "./product-tile.module.css";

export interface ProductTileProps {
  product: FrontendProduct;
  buttonText: string;
  onButtonClick: (
    product: FrontendProduct,
    variantId: string,
    quantity: number,
  ) => void | Promise<void>;
  readOnly?: boolean;
  onImageClick?: (product: FrontendProduct, quantity: number) => void;
  topRightButton?: ReactNode;
  showQuantity?: boolean;
  onImageError?: (product: FrontendProduct) => void;
}

export const ProductTile = memo(function ProductTile({
  product,
  buttonText,
  onButtonClick,
  readOnly = false,
  onImageClick,
  topRightButton,
  showQuantity = false,
  onImageError,
}: ProductTileProps) {
  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);

  const handleImageError = useHandler(() => {
    setImageError(true);
    onImageError?.(product);
  });

  const handleImageClick = useHandler(() => {
    if (onImageClick) {
      onImageClick(product, quantity);
    }
  });

  const priceRangeString = useMemo(() => {
    if (product.variants.length === 0) return undefined;

    const prices = product.variants
      .map((v) => v.price)
      .filter((p): p is number => p !== null && p !== undefined);

    if (prices.length === 0) return undefined;

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) {
      return `$${minPrice.toFixed(2)}`;
    } else {
      return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
    }
  }, [product.variants]);

  const variantOptions: RedoListItem<{ id: string; title: string }>[] =
    useMemo(() => {
      return product.variants.map((variant) => {
        const variantText =
          variant.attributes
            ?.map((attr) => `${attr.name}: ${attr.value}`)
            .join(" / ") || "Default";
        const displayText = variant.available
          ? variantText
          : `${variantText} (Out of Stock)`;

        return {
          id: variant.externalId,
          value: { id: variant.externalId, title: variantText },
          type: "text",
          text: displayText,
        };
      });
    }, [product.variants]);

  const [selectedOption, setSelectedOption] = useState<
    RedoListItem<{ id: string; title: string }> | undefined
  >(() => {
    // Select the first available variant, or the first variant if none are available
    const firstAvailableVariant = product.variants.find((v) => v.available);
    const targetVariantId =
      firstAvailableVariant?.externalId || product.variants[0]?.externalId;
    return variantOptions.find((opt) => opt.id === targetVariantId);
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (showSuccess) {
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }
  }, [showSuccess]);

  const handleButtonClick = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      if (selectedOption?.value.id) {
        try {
          setIsProcessing(true);
          await onButtonClick(product, selectedOption.value.id, quantity);
          setShowSuccess(true);
        } catch (error) {
          console.error(error);
        } finally {
          setIsProcessing(false);
        }
      }
    },
    [product, selectedOption, quantity, onButtonClick],
  );

  if (imageError) {
    return null;
  }

  return (
    <Flex
      as={readOnly ? "div" : "button"}
      className={productTileCss.productTileContainer}
      dir="column"
      gap="lg"
      radius="sm"
      style={{ position: "relative" }}
    >
      {topRightButton && (
        <Flex
          style={{ position: "absolute", top: "8px", right: "8px", zIndex: 10 }}
        >
          {topRightButton}
        </Flex>
      )}
      <Flex dir="column" gap="lg">
        <Flex
          className={productTileCss.productTileImageContainer}
          onClick={handleImageClick}
        >
          <img
            alt={product.name}
            className={productTileCss.productTileImage}
            onError={handleImageError}
            src={
              product.images?.[0]?.url || product.variants[0]?.images?.[0]?.url
            }
          />
        </Flex>

        <Text
          className={productTileCss.productTileTitle}
          color="primary"
          fontSize="sm"
          fontWeight="semibold"
          px="xl"
        >
          {product.name}
        </Text>
        <Flex align="center" dir="row" gap="sm" justify="space-between" px="xl">
          <Text color="secondary" fontSize="xs" fontWeight="regular">
            {priceRangeString}
          </Text>
          {showQuantity && (
            <Flex style={{ width: "60px" }}>
              <RedoTextInput
                inputTypeHint="number"
                placeholder="Qty"
                setValue={(value) => {
                  const num = parseInt(value);
                  if (!isNaN(num) && num > 0) {
                    setQuantity(num);
                  }
                }}
                size="sm"
                value={quantity.toString()}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      {!!product.variants.length && (
        <Flex dir="column" gap="md" pb="md" pt="sm" px="xl">
          <RedoSingleSelectDropdownInput
            disabledItems={product.variants
              .filter((v) => !v.available)
              .map((v) => v.externalId)}
            disabledTooltips={product.variants
              .filter((v) => !v.available)
              .reduce(
                (acc, v) => {
                  acc[v.externalId] = "Out of stock";
                  return acc;
                },
                {} as Record<string, string>,
              )}
            options={variantOptions}
            optionSelected={({ item }) => {
              setSelectedOption(item);
            }}
            placeholder="Select variant"
            selectedItem={selectedOption}
          />
          <Flex pb="md">
            <RedoButton
              className={productTileCss.addToCartButton}
              disabled={readOnly || isProcessing}
              hierarchy="primary"
              IconLeading={showSuccess ? CheckIcon : undefined}
              onClick={handleButtonClick}
              pending={isProcessing}
              size="sm"
              text={showSuccess ? "Added" : buttonText}
              theme="normal"
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
